import { UIColor } from "uicore-ts"


export class BSColor extends UIColor {
    
    
    // --- Primary (Bootstrap Blue — always #0d6efd regardless of application mode) ---
    
    /**
     * The primary tint color. Always Bootstrap blue (#0d6efd).
     * Application-mode theming (procurer vs offerer) is handled at the
     * component level (e.g. TopBarView), not here.
     *
     * Derivation multipliers reverse-engineered from Bootstrap 5 source values:
     *   Hover        × 0.850
     *   Active       × 0.800
     *   BorderActive × 0.752
     *   Shadow       base.colorWithAlpha(0.5)
     *   FocusRing    base.colorWithAlpha(0.25)
     */
    static readonly _primary = "#0d6efd"
    
    private static get _primaryBase(): BSColor {
        return new BSColor(BSColor._primary)
    }
    
    static get primary(): UIColor {
        return BSColor._primaryBase.withSemanticKey("primary", BSColor)
    }
    
    static get primaryHover(): UIColor {
        return BSColor._primaryBase.colorByMultiplyingRGB(0.850).withSemanticKey("primaryHover", BSColor)
    }
    
    static get primaryActive(): UIColor {
        return BSColor._primaryBase.colorByMultiplyingRGB(0.800).withSemanticKey("primaryActive", BSColor)
    }
    
    static get primaryBorder(): UIColor {
        return BSColor._primaryBase.withSemanticKey("primaryBorder", BSColor)
    }
    
    static get primaryBorderHover(): UIColor {
        return BSColor._primaryBase.colorByMultiplyingRGB(0.800).withSemanticKey("primaryBorderHover", BSColor)
    }
    
    static get primaryBorderActive(): UIColor {
        return BSColor._primaryBase.colorByMultiplyingRGB(0.752).withSemanticKey("primaryBorderActive", BSColor)
    }
    
    static get primaryShadow(): UIColor {
        return BSColor._primaryBase.colorWithAlpha(0.5).withSemanticKey("primaryShadow", BSColor)
    }
    
    // Focus ring: primary base at 25% opacity
    static get focusRing(): UIColor {
        return BSColor._primaryBase.colorWithAlpha(0.25).withSemanticKey("focusRing", BSColor)
    }
    
    
    // --- Secondary (Gray) ---
    
    static readonly _secondary = "#6c757d"
    
    static get secondary(): UIColor {
        return new BSColor(BSColor._secondary).withSemanticKey("secondary", BSColor)
    }
    
    static get secondaryHover(): UIColor {
        return new BSColor("#5c636a").withSemanticKey("secondaryHover", BSColor)
    }
    
    static get secondaryActive(): UIColor {
        return new BSColor("#565e64").withSemanticKey("secondaryActive", BSColor)
    }
    
    static get secondaryBorder(): UIColor {
        return new BSColor(BSColor._secondary).withSemanticKey("secondaryBorder", BSColor)
    }
    
    static get secondaryBorderHover(): UIColor {
        return new BSColor("#565e64").withSemanticKey("secondaryBorderHover", BSColor)
    }
    
    static get secondaryBorderActive(): UIColor {
        return new BSColor("#51585e").withSemanticKey("secondaryBorderActive", BSColor)
    }
    
    static get secondaryShadow(): UIColor {
        return new BSColor("rgba(130, 138, 145, 0.5)").withSemanticKey("secondaryShadow", BSColor)
    }
    
    
    // --- Success (Green) ---
    
    static readonly _success = "#198754"
    
    static get success(): UIColor {
        return new BSColor(BSColor._success).withSemanticKey("success", BSColor)
    }
    
    static get successHover(): UIColor {
        return new BSColor("#157347").withSemanticKey("successHover", BSColor)
    }
    
    static get successActive(): UIColor {
        return new BSColor("#146c43").withSemanticKey("successActive", BSColor)
    }
    
    static get successBorder(): UIColor {
        return new BSColor(BSColor._success).withSemanticKey("successBorder", BSColor)
    }
    
    static get successBorderHover(): UIColor {
        return new BSColor("#146c43").withSemanticKey("successBorderHover", BSColor)
    }
    
    static get successBorderActive(): UIColor {
        return new BSColor("#13653f").withSemanticKey("successBorderActive", BSColor)
    }
    
    static get successShadow(): UIColor {
        return new BSColor("rgba(60, 153, 110, 0.5)").withSemanticKey("successShadow", BSColor)
    }
    
    
    // --- Danger (Red) ---
    
    static readonly _danger = "#dc3545"
    
    static get danger(): UIColor {
        return new BSColor(BSColor._danger).withSemanticKey("danger", BSColor)
    }
    
    static get dangerHover(): UIColor {
        return new BSColor("#bb2d3b").withSemanticKey("dangerHover", BSColor)
    }
    
    static get dangerActive(): UIColor {
        return new BSColor("#b02a37").withSemanticKey("dangerActive", BSColor)
    }
    
    static get dangerBorder(): UIColor {
        return new BSColor(BSColor._danger).withSemanticKey("dangerBorder", BSColor)
    }
    
    static get dangerBorderHover(): UIColor {
        return new BSColor("#b02a37").withSemanticKey("dangerBorderHover", BSColor)
    }
    
    static get dangerBorderActive(): UIColor {
        return new BSColor("#a52834").withSemanticKey("dangerBorderActive", BSColor)
    }
    
    static get dangerShadow(): UIColor {
        return new BSColor("rgba(225, 83, 97, 0.5)").withSemanticKey("dangerShadow", BSColor)
    }
    
    
    // --- Warning ---
    
    static readonly _warningOrange = "#f59e0b"
    
    static get warningOrange(): UIColor {
        return new BSColor(BSColor._warningOrange).withSemanticKey("warningOrange", BSColor)
    }
    
    static readonly _warning = "#ffc107"
    
    static get warning(): UIColor {
        return new BSColor(BSColor._warning).withSemanticKey("warning", BSColor)
    }
    
    static get warningHover(): UIColor {
        return new BSColor("#ffca2c").withSemanticKey("warningHover", BSColor)
    }
    
    static get warningActive(): UIColor {
        return new BSColor("#ffcd39").withSemanticKey("warningActive", BSColor)
    }
    
    static get warningBorder(): UIColor {
        return new BSColor(BSColor._warning).withSemanticKey("warningBorder", BSColor)
    }
    
    static get warningBorderHover(): UIColor {
        return new BSColor("#ffc720").withSemanticKey("warningBorderHover", BSColor)
    }
    
    static get warningBorderActive(): UIColor {
        return new BSColor("#ffc720").withSemanticKey("warningBorderActive", BSColor)
    }
    
    static get warningShadow(): UIColor {
        return new BSColor("rgba(217, 164, 6, 0.5)").withSemanticKey("warningShadow", BSColor)
    }
    
    
    // --- Info (Cyan) ---
    
    static readonly _info = "#0dcaf0"
    
    static get info(): UIColor {
        return new BSColor(BSColor._info).withSemanticKey("info", BSColor)
    }
    
    static get infoHover(): UIColor {
        return new BSColor("#31d2f2").withSemanticKey("infoHover", BSColor)
    }
    
    static get infoActive(): UIColor {
        return new BSColor("#3dd5f3").withSemanticKey("infoActive", BSColor)
    }
    
    static get infoBorder(): UIColor {
        return new BSColor(BSColor._info).withSemanticKey("infoBorder", BSColor)
    }
    
    static get infoBorderHover(): UIColor {
        return new BSColor("#25cff2").withSemanticKey("infoBorderHover", BSColor)
    }
    
    static get infoBorderActive(): UIColor {
        return new BSColor("#25cff2").withSemanticKey("infoBorderActive", BSColor)
    }
    
    static get infoShadow(): UIColor {
        return new BSColor("rgba(11, 172, 204, 0.5)").withSemanticKey("infoShadow", BSColor)
    }
    
    
    // --- Light ---
    
    static readonly _light = "#f8f9fa"
    
    static get light(): UIColor {
        return new BSColor(BSColor._light).withSemanticKey("light", BSColor)
    }
    
    static get lightHover(): UIColor {
        return new BSColor("#e2e6ea").withSemanticKey("lightHover", BSColor)
    }
    
    static get lightActive(): UIColor {
        return new BSColor("#dae0e5").withSemanticKey("lightActive", BSColor)
    }
    
    static get lightBorder(): UIColor {
        return new BSColor(BSColor._light).withSemanticKey("lightBorder", BSColor)
    }
    
    static get lightBorderHover(): UIColor {
        return new BSColor("#dae0e5").withSemanticKey("lightBorderHover", BSColor)
    }
    
    static get lightBorderActive(): UIColor {
        return new BSColor("#d3d9df").withSemanticKey("lightBorderActive", BSColor)
    }
    
    static get lightShadow(): UIColor {
        return new BSColor("rgba(211, 212, 213, 0.5)").withSemanticKey("lightShadow", BSColor)
    }
    
    
    // --- Dark ---
    
    static readonly _dark = "#212529"
    
    static get dark(): UIColor {
        return new BSColor(BSColor._dark).withSemanticKey("dark", BSColor)
    }
    
    static get darkHover(): UIColor {
        return new BSColor("#424649").withSemanticKey("darkHover", BSColor)
    }
    
    static get darkActive(): UIColor {
        return new BSColor("#4d5154").withSemanticKey("darkActive", BSColor)
    }
    
    static get darkBorder(): UIColor {
        return new BSColor(BSColor._dark).withSemanticKey("darkBorder", BSColor)
    }
    
    static get darkBorderHover(): UIColor {
        return new BSColor("#373b3e").withSemanticKey("darkBorderHover", BSColor)
    }
    
    static get darkBorderActive(): UIColor {
        return new BSColor("#373b3e").withSemanticKey("darkBorderActive", BSColor)
    }
    
    static get darkShadow(): UIColor {
        return new BSColor("rgba(66, 70, 73, 0.5)").withSemanticKey("darkShadow", BSColor)
    }
    
    
    // --- Text colors ---
    
    static readonly _textWhite = "#ffffff"
    
    static get textWhite(): UIColor {
        return new BSColor(BSColor._textWhite).withSemanticKey("textWhite", BSColor)
    }
    
    static readonly _textBlack = "#000000"
    
    static get textBlack(): UIColor {
        return new BSColor(BSColor._textBlack).withSemanticKey("textBlack", BSColor)
    }
    
    static readonly _textMuted = "#6c757d"
    
    static get textMuted(): UIColor {
        return new BSColor(BSColor._textMuted).withSemanticKey("textMuted", BSColor)
    }
    
    
    // --- Utility ---
    
    static readonly _disabled = "#e9ecef"
    
    static get disabled(): UIColor {
        return new BSColor(BSColor._disabled).withSemanticKey("disabled", BSColor)
    }
    
    static readonly _borderLight = "#dee2e6"
    
    // Standard border color usually used on cards and inputs (#dee2e6)
    static get borderLight(): UIColor {
        return new BSColor(BSColor._borderLight).withSemanticKey("borderLight", BSColor)
    }
    
    static readonly _backgroundLight = "#f8f9fa"
    
    // Lighter background for hover states (#f8f9fa)
    static get backgroundLight(): UIColor {
        return new BSColor(BSColor._backgroundLight).withSemanticKey("backgroundLight", BSColor)
    }
    
    
}
